/**
 * A barra de recorte dos relatórios (#557, redesenho de out/2026).
 *
 * As quatro telas recortam pelo mesmo eixo — o período — e cada uma acrescenta
 * os seus (funil, equipe, base de comparação, corte de parada). Antes o período
 * morava nas ações do cabeçalho e os outros filtros num bloco à parte, dentro
 * do corpo que rola: dois lugares para a mesma pergunta, "o que estou vendo?".
 *
 * Aqui eles moram juntos, na fileira `extra` do cabeçalho — que não rola —, e a
 * barra termina dizendo DE QUEM são os números. É o que a pessoa precisa saber
 * antes de ler qualquer um deles.
 */

import type { ReactNode } from 'react';
import { Chip, Segmentado } from '@/shared/ui';
import { PERIODOS, type ChaveDePeriodo } from '../lib/periodo';

export type BarraDoRelatorioProps = {
  periodo: ChaveDePeriodo;
  aoEscolherPeriodo: (periodo: ChaveDePeriodo) => void;
  /** Os filtros próprios da tela, depois do período. */
  children?: ReactNode;
  /** De quem são os números, em palavras: "toda a organização", "seu time". */
  abrangencia?: string;
  /** O que vem antes da abrangência: "Números de", "Volume de". */
  rotuloDaAbrangencia?: string;
};

export function BarraDoRelatorio({
  periodo,
  aoEscolherPeriodo,
  children,
  abrangencia,
  rotuloDaAbrangencia = 'Números de',
}: BarraDoRelatorioProps): ReactNode {
  return (
    <div className="relatorio__barra">
      <Segmentado<ChaveDePeriodo>
        como="grupo"
        rotulo="Período"
        opcoes={PERIODOS.map((opcao) => ({ valor: opcao.chave, rotulo: opcao.nome }))}
        escolhida={periodo}
        aoEscolher={aoEscolherPeriodo}
      />
      {children}
      {abrangencia && (
        <span className="relatorio__de-quem">
          {rotuloDaAbrangencia} <Chip>{abrangencia}</Chip>
        </span>
      )}
    </div>
  );
}

/** O título de um cartão de relatório, com a abrangência ao lado quando ela muda. */
export function TituloDoCartao({
  id,
  children,
  abrangencia,
  apoio,
}: {
  id: string;
  children: ReactNode;
  abrangencia?: string;
  apoio?: ReactNode;
}): ReactNode {
  return (
    <div className="relatorio__cartao-topo">
      <h2 id={id} className="relatorio__titulo">
        {children}
      </h2>
      {abrangencia && <Chip>{abrangencia}</Chip>}
      {apoio && <span className="relatorio__cartao-apoio">{apoio}</span>}
    </div>
  );
}
